import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePortalAuth } from "../../lib/portal-auth-context";
import {
  FilePlus,
  Receipt,
  Truck,
  CurrencyDollar,
  ChatCircle,
  ArrowRight,
  Info,
} from "@phosphor-icons/react";

const StatCard = ({ label, value, sub, to, Icon, testId }) => (
  <Link
    to={to || "#"}
    data-testid={testId}
    className="group block bg-white border border-slate-200 p-6 hover:border-[var(--icor-blue)] transition-colors"
    style={{ borderRadius: 4 }}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-slate-500">
          {label}
        </div>
        <div className="font-display text-3xl md:text-4xl font-semibold tracking-tighter mt-2 text-[var(--icor-navy)]">
          {value}
        </div>
        {sub && (
          <div className="text-xs text-slate-500 mt-1.5">{sub}</div>
        )}
      </div>
      <div className="p-2 bg-slate-50 border border-slate-200 text-[var(--icor-blue)] group-hover:bg-[var(--icor-blue)] group-hover:text-white transition-colors">
        <Icon size={18} weight="duotone" />
      </div>
    </div>
  </Link>
);

const formatMXN = (n) =>
  `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

export default function PortalDashboard() {
  const { user, api } = usePortalAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard")
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [api]);

  return (
    <div data-testid="portal-dashboard-page">
      <div>
        <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
          Resumen
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter mt-3 text-[var(--icor-navy)]">
          Hola, {user.full_name.split(" ")[0]}.
        </h1>
        <p className="text-sm text-slate-500 mt-2 max-w-xl">
          {data?.client_name
            ? `Estás operando como ${data.client_name}.`
            : "Bienvenido a tu Portal Cliente ICOR."}
        </p>
      </div>

      {!loading && data && !data.linked && (
        <div
          className="mt-8 p-5 bg-amber-50 border border-amber-200 text-amber-900 flex gap-3 items-start"
          style={{ borderRadius: 4 }}
          data-testid="portal-dashboard-unlinked"
        >
          <Info size={20} weight="duotone" className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-sm">
              Tu cuenta aún no está vinculada a una empresa cliente.
            </div>
            <div className="text-sm mt-1 leading-relaxed">
              Un administrador la vinculará pronto para que veas tus pedidos,
              envíos y facturas. Mientras tanto puedes solicitar una nueva
              cotización.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <StatCard
          label="Mis pedidos"
          value={loading ? "…" : data?.orders_count ?? 0}
          sub={
            data?.orders_pending
              ? `${data.orders_pending} pendientes`
              : "Sin pendientes"
          }
          Icon={Receipt}
          to="/portal/orders"
          testId="portal-stat-orders"
        />
        <StatCard
          label="Envíos activos"
          value={loading ? "…" : data?.shipments_active ?? 0}
          sub="En tránsito o por salir"
          Icon={Truck}
          to="/portal/shipments"
          testId="portal-stat-shipments"
        />
        <StatCard
          label="Por pagar"
          value={loading ? "…" : formatMXN(data?.invoices_outstanding)}
          sub="Facturas abiertas"
          Icon={CurrencyDollar}
          to="/portal/invoices"
          testId="portal-stat-invoices"
        />
        <StatCard
          label="Cotizaciones"
          value={loading ? "…" : data?.quotes_count ?? 0}
          sub="Solicitudes enviadas"
          Icon={ChatCircle}
          to="/portal/quotes"
          testId="portal-stat-quotes"
        />
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <Link
          to="/portal/new-quote"
          className="group bg-[var(--icor-navy)] text-white p-7 flex items-center justify-between hover:bg-[var(--icor-blue)] transition-colors"
          style={{ borderRadius: 4 }}
          data-testid="portal-cta-new-quote"
        >
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] group-hover:text-white">
              Acción rápida
            </div>
            <div className="font-display text-xl font-semibold tracking-tight mt-2">
              Solicitar una nueva cotización
            </div>
            <p className="text-sm text-white/70 mt-1.5 max-w-sm">
              Recibe propuesta detallada de tu asesor en menos de 24 horas.
            </p>
          </div>
          <FilePlus size={32} weight="duotone" />
        </Link>

        <Link
          to="/portal/catalog"
          className="group bg-white border border-slate-200 p-7 flex items-center justify-between hover:border-[var(--icor-blue)] transition-colors"
          style={{ borderRadius: 4 }}
          data-testid="portal-cta-catalog"
        >
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)]">
              Catálogo
            </div>
            <div className="font-display text-xl font-semibold tracking-tight mt-2 text-[var(--icor-navy)]">
              Ver productos con tu precio
            </div>
            <p className="text-sm text-slate-500 mt-1.5 max-w-sm">
              Precios y disponibilidad personalizados para tu cuenta.
            </p>
          </div>
          <ArrowRight size={22} className="text-[var(--icor-blue)]" />
        </Link>
      </div>
    </div>
  );
}
